import React from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { RiskStatistics } from "@sscs/report/ui";
import { convertFinalCheckLabel } from "@sscs/report/utils";
import {
  CheckLabel,
  Link,
  LongTextLabel,
  Table,
  ValueOrPlaceholder,
} from "@sscs/ui";
import { LinkOffIcon, SettingsIcon } from "@sscs/ui/icons";
import { ActionMenu, AllowedTo, ApprovalStatus } from "Components";
import { PERMISSION } from "Constants";
import { useDialog } from "Hooks";
import { ActionItem, Package } from "Types";
import ConfigureImport from "../../components/ConfigureImport/ConfigureImport";
import CreatePackage from "../../components/CreatePackage/CreatePackage";
import DeletePackageDialog from "../../components/DeletePackageDialog/DeletePackageDialog";
import DetachFromCatalog from "../../components/DetachFromCatalog/DetachFromCatalog";
import styles from "./projectsContent.module.scss";

type PackageRowProps = {
  rowIndex: number;
  packageInfo: Package;
  projectName: string;
};

const PackageRow = ({
  rowIndex,
  packageInfo,
  projectName,
}: PackageRowProps): React.JSX.Element => {
  const deleteDialog = useDialog();
  const editDialog = useDialog();
  const configureImportDialog = useDialog();
  const detachDialog = useDialog();
  const { status, scanLevel, isRepro, isDiff } = convertFinalCheckLabel(
    packageInfo.analysis_overview?.final_scan_report.checks,
  );
  const isLevelCustomized =
    packageInfo.analysis_overview?.final_scan_report.inhibitors.customized;

  const actions: ActionItem[] = [
    {
      id: "edit-package",
      type: "EDIT",
      text: "Edit package",
      onClick: editDialog.open,
      permissions: [PERMISSION.CREATE_PACKAGE],
    },
    ...(packageInfo.external_catalog
      ? [
          {
            id: "configure-import",
            type: "CUSTOM" as const,
            itemIcon: <SettingsIcon />,
            text: "Configure catalog import",
            onClick: configureImportDialog.open,
            permissions: [PERMISSION.CREATE_PACKAGE],
          },
          {
            id: "detach-from-catalog",
            type: "CUSTOM" as const,
            itemIcon: <LinkOffIcon />,
            text: "Detach from catalog",
            onClick: detachDialog.open,
            permissions: [PERMISSION.CREATE_PACKAGE],
          },
        ]
      : []),
    {
      id: "delete-package",
      type: "DELETE",
      text: "Delete package",
      onClick: deleteDialog.open,
      permissions: [PERMISSION.DELETE_PACKAGE],
    },
  ];

  return (
    <>
      <Table.Row>
        <Table.Cell>
          <CheckLabel
            status={status}
            scanLevel={scanLevel}
            isLevelCustomized={isLevelCustomized}
            isReproducibilityCheck={isRepro}
            isDiffCheck={isDiff}
          />
        </Table.Cell>
        <Table.Cell>
          <ApprovalStatus
            status={packageInfo.approval_status}
            info={packageInfo.approval_information}
          />
        </Table.Cell>
        <Table.Cell>
          <Link
            id={`package-${rowIndex}`}
            to={`package/${packageInfo.package_id}`}
            underline="always"
            className={styles["package-name"]}
          >
            <LongTextLabel
              label={packageInfo.name}
              title={packageInfo.name}
              className={styles.name}
            />
          </Link>
        </Table.Cell>
        <Table.Cell>
          <RiskStatistics
            risk={packageInfo.analysis_overview?.final_scan_report.assessments}
          />
        </Table.Cell>
        <Table.Cell>
          <ValueOrPlaceholder
            value={
              packageInfo.version_count
                ? `${packageInfo.version_count} versions`
                : undefined
            }
          />
        </Table.Cell>
        <Table.Cell>
          <ValueOrPlaceholder
            value={
              packageInfo.last_scan
                ? `${formatDistanceToNowStrict(new Date(packageInfo.last_scan))} ago`
                : undefined
            }
          />
        </Table.Cell>
        <AllowedTo permissions={[PERMISSION.CREATE_PACKAGE]}>
          <Table.Cell align="center">
            <ActionMenu
              id={`package-row-actions-${rowIndex}`}
              items={actions}
            />
          </Table.Cell>
        </AllowedTo>
      </Table.Row>
      <DeletePackageDialog
        isOpen={deleteDialog.isOpen}
        packageDataOrId={packageInfo}
        projectName={projectName}
        onClose={deleteDialog.close}
      />
      <CreatePackage
        isOpen={editDialog.isOpen}
        projectId={packageInfo.project_id}
        projectName={projectName}
        packageInfo={packageInfo}
        onClose={editDialog.close}
      />
      {packageInfo.external_catalog && (
        <>
          <ConfigureImport
            isOpen={configureImportDialog.isOpen}
            packageId={packageInfo.package_id}
            packageName={packageInfo.name}
            externalPackage={packageInfo.external_catalog}
            onClose={configureImportDialog.close}
          />
          <DetachFromCatalog
            isOpen={detachDialog.isOpen}
            packageId={packageInfo.package_id}
            packageName={packageInfo.name}
            catalogName={packageInfo.external_catalog.catalog_display_name}
            onClose={detachDialog.close}
          />
        </>
      )}
    </>
  );
};

export default PackageRow;
